import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { GetLogItemsByObjectId } from 'src/app/logbook/logbook.actions';
import { LogbookState } from 'src/app/logbook/logbook.state';
import LogItemsForObject from 'src/app/models/Logbook/LogItemsForObject';
import LogItem from 'src/app/models/Logbook/LogItem';

export interface AIResult {
  vatAmount?: number | null;
  // vatPercent can be a number or the string 'OTHER' when the rate is not one of the allowed values
  vatPercent?: number | 'OTHER' | null;
  amountIncl?: number | null;
  invoiceNumber?: string | null;
  contactName?: string | null;
  reason?: string | null;
  confidence?: number | null;
  rawText?: string | null;
}

export interface AIInvoiceContractFieldsResult {
  poNumber?: string | null;
  sesNumber?: string | null;
  contractAgreementNumber?: string | null;
  freeText?: string | null;
  pricePerHour?: number | null;
  budget?: number | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  confidence?: number | null;
  reason?: string | null;
}

export interface AIProjectInvoicingTimeResult {
  invoiceByDay?: boolean | null;
  poRequired?: boolean | null;
  paymentTermDays?: number | null;
  timesheetRequired?: boolean | null;
  timesheetType?: string | null;
  hoursPerDay?: number | null;
  confidence?: number | null;
  reason?: string | null;
}

/** Eén allocatielijn zoals AI die voorstelt. */
export type AllocationLine = {
  amount: number; // excl. btw
  // allow allocating to budgets, overheadCategories, projects or employees
  type: 'budget' | 'overheadCategory' | 'project' | 'employee';
  targetId: string; // id zoals aanwezig in jouw select-data
  note?: string | null;
};

/** Volledig AI-resultaat voor allocaties (meerdere lijnen mogelijk). */
export type AIAllocationsResult = {
  allocations: AllocationLine[];
  confidence?: number | null;
  reason?: string | null;
};

export type AIMarAccountLinkSuggestion = {
  overheadCategoryId: string;
  accountNumber: string;
  reason?: string | null;
  confidence?: number | null;
};

@Injectable({ providedIn: 'root' })
export class AIService {
  // Model is now configured server-side via Yuki Settings; no need to send it from the frontend.
  private readonly httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
  private jsonOptions() {
    const headersObj: Record<string, string> = { 'Content-Type': 'application/json' };
    const client = (environment as any).OPENAI_CLIENT_AUTH;
    if (client && String(client).trim().length) headersObj['X-Client-Auth'] = String(client).trim();
    return { headers: new HttpHeaders(headersObj) };
  }

  // Custom prompt in localStorage
  private static readonly CUSTOM_PROMPT_KEY = 'ai_custom_prompt';

  constructor(private store: Store, private http: HttpClient) {}

  // ===== Backend key status =====
  private async ensureOpenAIConfigured(): Promise<void> {
    try {
      const status = await firstValueFrom(this.http.get<{ configured: boolean }>(`${environment.baseUrl}/openai/key/status`, this.jsonOptions()));
      if (!status?.configured) throw new Error('OpenAI integratie is niet geconfigureerd. (Key ontbreekt in backend)');
    } catch (e: any) {
      // If key-status is restricted by permissions (403/401), proceed: downstream calls will fail if truly unconfigured
      const code = Number(e?.status || 0);
      if (code === 403 || code === 401) return;
      throw new Error('OpenAI integratie is niet geconfigureerd of niet bereikbaar.');
    }
  }

  // ===== Custom prompt beheer =====
  getCustomPrompt(): string | null {
    const v = localStorage.getItem(AIService.CUSTOM_PROMPT_KEY);
    return v && v.trim().length ? v : null;
  }

  setCustomPrompt(value: string | null): void {
    if (value && value.trim().length) {
      localStorage.setItem(AIService.CUSTOM_PROMPT_KEY, value.trim());
    } else {
      localStorage.removeItem(AIService.CUSTOM_PROMPT_KEY);
    }
  }

  async openPromptEditor(current?: string | null): Promise<string | null> {
    const existing = typeof current === 'string' ? current : (this.getCustomPrompt() ?? '');
    const updated = window.prompt('Edit AI classification prompt override (leave empty to remove):', existing ?? '');
    if (updated === null) return null;
    const normalized = updated.trim();
    this.setCustomPrompt(normalized || null);
    return normalized || null;
  }

  // ===== Helpers =====
  private extractBase64FromDataUrl(dataUrl: string): string | null {
    try {
      const idx = dataUrl.indexOf(',');
      if (idx > 0) return dataUrl.slice(idx + 1);
      return null;
    } catch { return null; }
  }

  /** Resolve a backend-relative URL to an absolute API URL without duplicating the host. */
  private resolveApiUrl(url: string): string {
    try {
      const u = (url || '').toString();
      // If already absolute (http/https), return as-is
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      // Only replace when the URL starts with '/api' to prevent mid-string replacement
      if (u.startsWith('/api')) {
        return u.replace('/api', environment.baseUrl);
      }
      // Otherwise, assume it's already relative to the API base
      return `${environment.baseUrl}${u.startsWith('/') ? '' : '/'}${u}`;
    } catch {
      return url;
    }
  }


  // ===== Prompts (BTW) =====
  private buildVatPromptFromPdfText(pdfText: string): string {
    return [
      'You are a strict VAT assistant for Belgian invoices.',
      'You receive raw text extracted from a PDF invoice.',
      '',
      'TASK:',
      '- Determine the VAT amount (vatAmount) and VAT rate (vatPercent).',
      '- Allowed vatPercent values: 0, 6, 12, 21; otherwise return "OTHER".',
      '- Prefer arithmetic consistency: total ≈ excl + vatAmount (2 decimals).',
      '- If multiple totals appear, use the final invoice total.',
      '- Read the invoice number.',
      '- Read the supplier/sender company name (contactName) — the company that SENT the invoice, not the recipient.',
      '',
      'OUTPUT JSON ONLY:',
      '{ "vatAmount": 0, "vatPercent": 21, "invoicenumber": "20250001", "contactName": "Company Name", "confidence": 0.95, "reason": "short" }',
      '',
      'PDF_TEXT_START',
      pdfText,
      'PDF_TEXT_END'
    ].join('\n');
  }

  private buildVatInstructionForImage(): string {
    return [
      'You are a strict VAT assistant for Belgian invoices.',
      'You will receive an invoice image.',
      '',
      'TASK:',
      '- Read totals and VAT from the image.',
      '- Determine the total invoice amount INCLUDING VAT (amountIncl).',
      '- Determine VAT amount (vatAmount) and VAT rate (vatPercent).',
      '- Allowed vatPercent values: 0, 6, 12, 21; otherwise "OTHER".',
      '- Prefer arithmetic consistency: amountIncl ≈ amountExcl + vatAmount (2 decimals).',
      '- Read the invoice number.',
      '- Read the supplier/sender company name (contactName) — the company that SENT the invoice, not the recipient.',
      '',
      'Return ONLY compact JSON:',
      '{ "amountIncl": 0, "vatAmount": 0, "vatPercent": 21, "invoicenumber": "20250001", "contactName": "Company Name", "confidence": 0.95, "reason": "short" }'
    ].join('\n');
  }

  // ===== OpenAI calls (BTW) =====
  async suggestVatFromPdfUrl(pdfUrlOrDataUrl: string): Promise<AIResult> {
    await this.ensureOpenAIConfigured();

    // Obtain base64 (only via backend retrieval or data URL); avoid direct S3 GET
    let pdfBase64: string | null = null;
    if (pdfUrlOrDataUrl.startsWith('data:application/pdf')) {
      pdfBase64 = this.extractBase64FromDataUrl(pdfUrlOrDataUrl);
    } else if (
      pdfUrlOrDataUrl.includes('/api/timesheets/retrieveFile/') ||
      pdfUrlOrDataUrl.includes('/api/timesheetsmanagement/retrieveFile/') ||
      pdfUrlOrDataUrl.includes('/api/download/retrieveFile')
    ) {
      const resolved = this.resolveApiUrl(pdfUrlOrDataUrl);
      const resp = await firstValueFrom(
        this.http.get<any>(resolved, { ...this.jsonOptions(), observe: 'response' } as any)
      );
      pdfBase64 = (resp as any)?.body?.pdf || null;
    }
    if (!pdfBase64) throw new Error('PDF bron niet ondersteund. Gebruik backend ophalen of data-URL.');

    // Upload via backend to OpenAI Files
    const uploadResp = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/files/upload`, { pdfBase64 }, this.jsonOptions()));
    let fileId: string | null = null;
    try {
      const data = typeof uploadResp === 'string' ? JSON.parse(uploadResp) : uploadResp;
      fileId = data?.id || data?.file?.id || null;
    } catch {}
    if (!fileId) throw new Error('Upload naar OpenAI mislukt (geen fileId).');

    // Prompt instructing to read VAT from the PDF file
    const prompt = [
      'You are a strict VAT assistant for Belgian invoices.',
      'You receive the invoice as an input_file.',
      '',
      'TASK:',
      '- Determine the total invoice amount INCLUDING VAT (amountIncl).',
      '- Determine the VAT amount (vatAmount) and VAT rate (vatPercent).',
      '- Allowed vatPercent values: 0, 6, 12, 21; otherwise return "OTHER".',
      '- Prefer arithmetic consistency: amountIncl ≈ amountExcl + vatAmount (2 decimals).',
      '- Read the invoice number.',
      '- Read the supplier/sender company name (contactName) — the company that SENT the invoice, not the recipient.',
      '',
      'Return ONLY compact JSON:',
      '{ "amountIncl": 0, "vatAmount": 0, "vatPercent": 21, "invoicenumber": "20250001", "contactName": "Company Name", "confidence": 0.95, "reason": "short" }'
    ].join('\n');

    const respText = await this.callResponsesJson({ prompt, fileId });
    return this.parseAIResultFromMessageContent(respText);
  }

  async suggestVatFromImageUrl(imageSrc: string): Promise<AIResult> {
    await this.ensureOpenAIConfigured();

    const blocks: any[] = [
      { type: 'text', text: this.buildVatInstructionForImage() },
      { type: 'image_url', image_url: { url: imageSrc } },
    ];

    const data = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/chat/allocations`, { blocks }, this.jsonOptions()));
    return this.parseAIResultFromMessageContent(data?.choices?.[0]?.message?.content);
  }

  // ===== Sublink / people extraction =====
  private buildSublinkPromptFromText(text: string): string {
    return [
      'You are an extraction assistant. Given the full text of an invoice, find any person names (developers, analysts, employees) that are mentioned on invoice lines together with the amount for that line.',
      'Return a single compact JSON object with a single array field "people" where each item is { "name": "Full Name", "amount": 123.45 }.',
      'If no person names appear on the invoice, return { "people": [] } .',
      '',
      'Be conservative: only include entries where a clear name and an amount are present on the same line or very near each other.',
      '',
      'OUTPUT ONLY the JSON object.',
      'INVOICE_TEXT_START',
      text,
      'INVOICE_TEXT_END',
    ].join('\n');
  }

  public async suggestSublinkCandidatesFromPdfUrl(pdfUrlOrDataUrl: string): Promise<Array<{ name: string; amount: number }>> {
    await this.ensureOpenAIConfigured();

    let pdfBase64: string | null = null;
    if (pdfUrlOrDataUrl.startsWith('data:application/pdf')) {
      pdfBase64 = this.extractBase64FromDataUrl(pdfUrlOrDataUrl);
    } else if (pdfUrlOrDataUrl.includes('/api/timesheets/retrieveFile/') || pdfUrlOrDataUrl.includes('/api/download/retrieveFile')) {
      const resolved = this.resolveApiUrl(pdfUrlOrDataUrl);
      const resp = await firstValueFrom(this.http.get<any>(resolved, this.jsonOptions()));
      pdfBase64 = resp?.pdf || null;
    }
    if (!pdfBase64) throw new Error('PDF bron niet ondersteund. Gebruik backend ophalen of data-URL.');

    const uploadResp = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/files/upload`, { pdfBase64 }, this.jsonOptions()));
    let fileId: string | null = null;
    try {
      const data = typeof uploadResp === 'string' ? JSON.parse(uploadResp) : uploadResp;
      fileId = data?.id || data?.file?.id || null;
    } catch {}
    if (!fileId) throw new Error('Upload naar OpenAI mislukt (geen fileId).');

    const prompt = [
      'You are an extraction assistant. Given the PDF invoice as input_file, find any person names (developers, analysts, employees) mentioned on invoice lines together with the amount for that line.',
      'Return ONLY JSON: { "people": [ { "name": "Full Name", "amount": 123.45 } ] } or { "people": [] }.',
      'Be conservative: include only entries where a clear name and an amount are present on the same line or very near each other.'
    ].join('\n');

    const content = await this.callResponsesJson({ prompt, fileId });
    const match = (content as string).match(/\{[\s\S]*\}/);
    if (!match) return [];
    try {
      const parsed = JSON.parse(match[0]);
      const items = Array.isArray(parsed.people) ? parsed.people : [];
      return items
        .map((it: any) => ({ name: (it.name || '').toString().trim(), amount: Number(it.amount || 0) }))
        .filter((it: any) => it.name && !Number.isNaN(it.amount) && Number(it.amount) > 0);
    } catch {
      return [];
    }
  }

  public async suggestSublinkCandidatesFromImageUrl(imageUrl: string): Promise<Array<{ name: string; amount: number }>> {
    await this.ensureOpenAIConfigured();

    const prompt = [
      'You will be provided an invoice image. Extract person names and amounts per line as JSON.',
      'Return { "people": [ { "name": "Full Name", "amount": 123.45 } ] } or { "people": [] }',
      'Return only JSON.'
    ].join('\n');

    const blocks: any[] = [
      { type: 'text', text: prompt },
      { type: 'image_url', image_url: { url: imageUrl } },
    ];

    const data = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/chat/allocations`, { blocks }, this.jsonOptions()));
    const content = data?.choices?.[0]?.message?.content ?? '';
    const match = (content as string).match(/\{[\s\S]*\}/);
    if (!match) return [];
    try {
      const parsed = JSON.parse(match[0]);
      const items = Array.isArray(parsed.people) ? parsed.people : [];
      return items
        .map((it: any) => ({ name: (it.name || '').toString().trim(), amount: Number(it.amount || 0) }))
        .filter((it: any) => it.name && !Number.isNaN(it.amount) && Number(it.amount) > 0);
    } catch {
      return [];
    }
  }

  // ====================== Files + Responses (PDF Upload) ======================
  /** Upload a PDF (fetched from URL) to OpenAI Files for assistants/responses use. */
  async uploadFileFromUrl(pdfUrl: string): Promise<string> {
    await this.ensureOpenAIConfigured();
    console.groupCollapsed('%c[AIService] UploadFileFromUrl', 'color:#2196f3;font-weight:bold;');
    console.debug('Source URL:', pdfUrl);

    // Only allow backend retrieval endpoints or data URLs
    let pdfBase64: string | null = null;
    if (pdfUrl.startsWith('data:application/pdf')) {
      pdfBase64 = this.extractBase64FromDataUrl(pdfUrl);
    } else if (
      pdfUrl.includes('/api/timesheets/retrieveFile/') ||
      pdfUrl.includes('/api/timesheetsmanagement/retrieveFile/') ||
      pdfUrl.includes('/api/download/retrieveFile')
    ) {
      const resolved = this.resolveApiUrl(pdfUrl);
      console.debug('Backend retrieve resolve:', resolved);
      const resp = await firstValueFrom(this.http.get<any>(resolved, { ...this.jsonOptions(), observe: 'response' } as any));
      const body = (resp as any)?.body;
      console.debug('Backend retrieve response keys:', Object.keys(body || {}));
      pdfBase64 = body?.pdf || null;
    } else if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
      // External URL (e.g. S3). Ask backend to retrieve bytes to avoid CORS.
      console.debug('Requesting backend to fetch external PDF...');
      const opts = { ...this.jsonOptions(), params: { url: pdfUrl } } as any;
      const resp = await firstValueFrom(
        this.http.get<any>(
          `${environment.baseUrl}/download/retrieveFile`,
          { ...opts, observe: 'response' }
        )
      );
      const body = (resp as any)?.body;
      console.debug('External retrieve response keys:', Object.keys(body || {}));
      pdfBase64 = body?.pdf || null;
    }
    if (!pdfBase64) throw new Error('PDF bron niet ondersteund. Gebruik backend ophalen of data-URL.');
    console.debug('pdfBase64 length:', (pdfBase64 || '').length);

    const uploadResp = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/files/upload`, { pdfBase64 }, this.jsonOptions()));
    console.debug('OpenAI files/upload raw response:', uploadResp);
    try {
      const data = typeof uploadResp === 'string' ? JSON.parse(uploadResp as any) : uploadResp;
      const id = data?.id || data?.file?.id;
      if (!id) throw new Error('OpenAI Files: no file id returned');
      console.info('[AIService] File uploaded to OpenAI, id:', id);
      return id as string;
    } catch {
      console.error('[AIService] Invalid upload response, could not parse file id:', uploadResp);
      throw new Error('OpenAI Files: invalid response');
    }
  }

  /** Upload a PDF provided as base64 string to OpenAI Files. */
  async uploadFileFromBase64(b64: string): Promise<string> {
    await this.ensureOpenAIConfigured();
    if (!b64 || typeof b64 !== 'string') throw new Error('Missing base64 PDF payload');

    const uploadResp = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/files/upload`, { pdfBase64: b64 }, this.jsonOptions()));
    try {
      const data = typeof uploadResp === 'string' ? JSON.parse(uploadResp) : uploadResp;
      const id = data?.id || data?.file?.id;
      if (!id) throw new Error('OpenAI Files: no file id returned');
      return id as string;
    } catch {
      throw new Error('OpenAI Files: invalid response');
    }
  }

  /** Validate a timesheet PDF via Responses API using an uploaded file id. */
  async validateTimesheetPdfWithResponses(args: {
    fileId: string;
    employeeName?: string;
    expectedHours?: number;
    uploadedMonthText?: string;
    timesheetTypeHint?: string;
  }): Promise<{ issues: string[]; type?: string; parsedHours?: number | null; pdfMonthISO?: string | null; hoursOk?: boolean; monthOk?: boolean; signed?: boolean; employeePresent?: boolean }> {
    await this.ensureOpenAIConfigured();
    console.groupCollapsed('%c[AIService] validateTimesheetPdfWithResponses', 'color:#673ab7;font-weight:bold;');
    console.debug('fileId:', args.fileId);
    console.debug('context:', {
      employeeName: args.employeeName,
      expectedHours: args.expectedHours,
      uploadedMonthText: args.uploadedMonthText,
      timesheetTypeHint: args.timesheetTypeHint
    });

    const prompt = [
      'ROL: Valideer een timesheet-PDF en geef ALLEEN compacte JSON terug.',
      'CONTROLES:',
      '- Vergelijk uren in PDF met expectedHours.',
      '- Vergelijk maand in PDF met uploadedMonthText.',
      '- Detecteer of het document ondertekend is.',
      '- Bevestig dat employeeName voorkomt in het document.',
      '- Detecteer timesheettype (bv. Cegeka, FOD, Deloitte, SD Worx).',
      '',
      'SCHRIJFWIJZE VAN FOUTEN:',
      '- Schrijf elk item als een korte, duidelijke zin in het Nederlands.',
      '- Geen proza of uitleg: maximaal 1 zin per item, zonder extra opmaak.',
      '- Indien er interpretatieproblemen zijn, vul het veld "errors" met korte meldingen.',
      '',
      'Context:',
      `employeeName: ${args.employeeName || ''}`,
      `expectedHours: ${typeof args.expectedHours === 'number' ? args.expectedHours.toFixed(2) : ''}`,
      `uploadedMonthText: ${args.uploadedMonthText || ''}`,
      `timesheetTypeHint: ${args.timesheetTypeHint || ''}`,
      '',
      'Geef ALLEEN JSON terug volgens schema:',
      JSON.stringify({
        issues: ['string'],
        errors: ['string'],
        type: 'string',
        parsedHours: 0,
        pdfMonthISO: 'YYYY-MM-01',
        checks: { hours_ok: true, month_ok: true, signed: true, employee_present: true }
      }),
    ].join('\n');

    let data: any;
    try {
      // Send context; backend builds the prompt server-side
      data = await firstValueFrom(this.http.post<any>(
        `${environment.baseUrl}/openai/responses/validate-timesheet`,
        {
          fileId: args.fileId,
          employeeName: args.employeeName,
          expectedHours: args.expectedHours,
          uploadedMonthText: args.uploadedMonthText,
          timesheetTypeHint: args.timesheetTypeHint
        },
        this.jsonOptions()
      ));
    } catch (e: any) {
      const errMsg = (e?.error?.message || e?.message || 'Backend Responses error').toString();
      console.error('[AIService] Responses validate request failed:', errMsg, e);
      console.groupEnd();
      return { issues: [`(validatiefout) ${errMsg}`], type: undefined, parsedHours: null, pdfMonthISO: null };
    }

    // Attempt to read the output text robustly
    let outText = '';
    // Direct text
    if (typeof data?.output_text === 'string') {
      outText = data.output_text;
    } else if (Array.isArray(data?.output)) {
      // Find the first message item and extract its output_text content
      try {
        const msgItem = data.output.find((it: any) => (it?.type || '').toLowerCase() === 'message');
        const parts: any[] = Array.isArray(msgItem?.content) ? msgItem.content : [];
        // Prefer explicit output_text
        const outputTextPart = parts.find((p: any) => (p?.type || '').toLowerCase() === 'output_text' && typeof p?.text === 'string');
        if (outputTextPart?.text) {
          outText = outputTextPart.text;
        } else {
          // Fallback: any part with a string `text`
          const anyTextPart = parts.find((p: any) => typeof p?.text === 'string');
          if (anyTextPart?.text) outText = anyTextPart.text;
        }
      } catch {}
    } else if (data?.choices?.[0]?.message?.content) {
      outText = data.choices[0].message.content; // fallback if proxying to chat
    }
    console.debug('[AIService] Responses output text length:', (outText || '').length);
    console.groupEnd();

    const match = (outText || '').match(/\{[\s\S]*\}/);
    if (!match) return { issues: ['(validatiefout) AI kon geen JSON resultaat leveren.'], type: undefined, parsedHours: null, pdfMonthISO: null };
    try {
      const parsed = JSON.parse(match[0]);
      const issues = Array.isArray(parsed.issues) ? parsed.issues.map((s: any) => String(s || '').trim()).filter((s: string) => !!s) : [];
      const errors = Array.isArray(parsed.errors) ? parsed.errors.map((s: any) => String(s || '').trim()).filter((s: string) => !!s) : [];
      const allIssues = [...issues, ...errors.map(e => `(validatiefout) ${e}`)];
      const type = parsed.type ? String(parsed.type) : undefined;
      const parsedHours = typeof parsed.parsedHours === 'number' ? Number(parsed.parsedHours) : null;
      const pdfMonthISO = typeof parsed.pdfMonthISO === 'string' ? parsed.pdfMonthISO : null;
      // Structured checks (optional)
      const checks = parsed.checks || {};
      let hoursOk = typeof checks.hours_ok === 'boolean' ? checks.hours_ok : undefined;
      let monthOk = typeof checks.month_ok === 'boolean' ? checks.month_ok : undefined;
      let signed = typeof checks.signed === 'boolean' ? checks.signed : undefined;
      let employeePresent = typeof checks.employee_present === 'boolean' ? checks.employee_present : undefined;

      // Fallback heuristics if checks are missing
      if (hoursOk === undefined && parsedHours !== null && typeof args.expectedHours === 'number') {
        const exp = Number(args.expectedHours);
        const diff = Math.abs(parsedHours - exp);
        hoursOk = diff < 0.51; // accept small rounding diff
      }
      if (monthOk === undefined && pdfMonthISO && (args.uploadedMonthText || '')) {
        try {
          const up = String(args.uploadedMonthText || '').toLowerCase();
          const isoYear = String(pdfMonthISO).slice(0, 4);
          const isoMonth = String(pdfMonthISO).slice(5, 7);
          const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
          const mIdx = Number(isoMonth) - 1;
          const mName = monthNames[mIdx] || '';
          monthOk = up.includes(mName) && up.includes(isoYear);
        } catch { monthOk = undefined; }
      }
      if (signed === undefined) {
        signed = !allIssues.some((s) => s.toLowerCase().includes('niet ondertekend'));
      }
      if (employeePresent === undefined) {
        const name = (args.employeeName || '').toLowerCase();
        employeePresent = !allIssues.some((s) => s.toLowerCase().includes('komt niet voor')) || (!!name && !name.trim());
      }

      return { issues: allIssues, type, parsedHours, pdfMonthISO, hoursOk, monthOk, signed, employeePresent };
    } catch {
      return { issues: ['(validatiefout) AI-resultaat was niet geldig JSON.'], type: undefined, parsedHours: null, pdfMonthISO: null };
    }
  }

  // ===== Helpers (BTW) =====
  private parseAIResultFromMessageContent(content: string | undefined): AIResult {
    const text = content || '';
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) throw new Error('AI response did not contain JSON.');
    let parsed = JSON.parse(match[0]) as any;

    // normalize known variant names
    if (!parsed.invoiceNumber && parsed.invoicenumber) parsed.invoiceNumber = parsed.invoicenumber;
    if (!parsed.contactName && parsed.contactname) parsed.contactName = parsed.contactname;
    if (!parsed.contactName && parsed.contact_name) parsed.contactName = parsed.contact_name;
    if (!parsed.contactName && parsed.supplierName) parsed.contactName = parsed.supplierName;

    if (typeof parsed.vatAmount !== 'number' || Number.isNaN(parsed.vatAmount)) {
      throw new Error('vatAmount missing or not a number.');
    }
    parsed.vatAmount = Number(parsed.vatAmount.toFixed(2));

    if (typeof parsed.amountIncl === 'number' && !Number.isNaN(parsed.amountIncl)) {
      parsed.amountIncl = Number(parsed.amountIncl.toFixed(2));
    }

    const allowed = ['OTHER', 0, 6, 12, 21] as const;
    if (!allowed.includes(parsed.vatPercent as any)) {
      parsed.vatPercent = 'OTHER';
    }
    return parsed as AIResult;
  }

  // ---- Generic Responses helper ----
  private async callResponsesJson(args: { prompt: string; fileId: string; model?: string }): Promise<string> {
    await this.ensureOpenAIConfigured();
    const payload = {
      model: args.model || undefined,
      input: [
        { role: 'system', content: 'You output ONLY compact valid JSON. Never prose.' },
        { role: 'user', content: [ { type: 'input_text', text: args.prompt }, { type: 'input_file', file_id: args.fileId } ] }
      ]
    } as any;
    const data = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/responses/json`, payload, this.jsonOptions()));
    // Try to get output_text or fallback to message.content
    let outText = '';
    if (typeof data?.output_text === 'string') {
      outText = data.output_text;
    } else if (Array.isArray(data?.output)) {
      try {
        const msgItem = data.output.find((it: any) => (it?.type || '').toLowerCase() === 'message');
        const parts: any[] = Array.isArray(msgItem?.content) ? msgItem.content : [];
        const outputTextPart = parts.find((p: any) => (p?.type || '').toLowerCase() === 'output_text' && typeof p?.text === 'string');
        if (outputTextPart?.text) outText = outputTextPart.text; else {
          const anyTextPart = parts.find((p: any) => typeof p?.text === 'string');
          if (anyTextPart?.text) outText = anyTextPart.text;
        }
      } catch {}
    } else if (data?.choices?.[0]?.message?.content) {
      outText = data.choices[0].message.content;
    }
    return outText || '';
  }

  async suggestInvoiceContractFieldsFromPdfUrl(pdfUrlOrDataUrl: string): Promise<AIInvoiceContractFieldsResult> {
    await this.ensureOpenAIConfigured();
    const fileId = await this.uploadFileFromUrl(pdfUrlOrDataUrl);

    const prompt = [
      'You are an extraction assistant for invoice references in contracts/invoices.',
      'Return ONLY compact valid JSON (no prose, no markdown).',
      'Extract values only when explicitly present in the document.',
      'If not found, return null for that field.',
      '',
      'Required JSON schema:',
      '{',
      '  "poNumber": string|null,',
      '  "sesNumber": string|null,',
      '  "contractAgreementNumber": string|null,',
      '  "freeText": string|null,',
      '  "pricePerHour": number|null,',
      '  "budget": number|null,',
      '  "effectiveFrom": string|null,',
      '  "effectiveTo": string|null,',
      '  "confidence": number|null,',
      '  "reason": string|null',
      '}',
      '',
      'Extraction rules:',
      '- poNumber: purchase order / PO reference required on invoice.',
      '- sesNumber: SES / approval-to-invoice / toelating tot facturering reference.',
      '- contractAgreementNumber: contract, aannemingsovereenkomst, framework/master service agreement number.',
      '- freeText: provide ONLY copy-paste-ready invoice text lines (Dutch/English as in source).',
      '- freeText: do NOT write instructions like "Invoice must reference...". Write only the actual text to place on the invoice.',
      '- pricePerHour: return hourly rate in EUR as numeric only (e.g. 85.5).',
      '- pricePerHour: detect synonyms like "uurprijs", "uurtarief", "hourly rate", "rate per hour", "€/uur", "EUR/hour".',
      '- If the document states a day rate (per day/dagtarief/jour), convert to hourly by dividing by 8 and return that hourly value.',
      '- budget: total contract budget/value ceiling in EUR as numeric only (e.g. 120000). If not explicitly present, return null.',
      '- budget: detect synonyms like "budget", "contractwaarde", "totale waarde", "maximum bedrag", "plafond", "not-to-exceed", "ceiling".',
      '- budget priority: prefer explicit total contract value/cap wording like "contractwaarde", "totale contractwaarde", "max budget", "budgetplafond", "not-to-exceed", "ceiling".',
      '- budget exclusion: do NOT use hourly/day rates, monthly fees, line-item amounts, VAT amounts or invoice totals as budget unless the text explicitly states it is the contract total/cap.',
      '- budget derivation (when no explicit cap is present):',
      '  - if maxDays and dayRate are explicit: budget = maxDays * dayRate.',
      '  - if maxDays and only hourlyRate are explicit: budget = maxDays * (hourlyRate * 8).',
      '  - if maxHours and hourlyRate are explicit: budget = maxHours * hourlyRate.',
      '  - use only clearly stated maxima (e.g. maximum aantal dagen/uren, capped at, tot max).',
      '  - if operands are missing or ambiguous, return budget as null.',
      '- effectiveFrom: start date of rate/contract validity. Return in YYYY-MM-DD format, else null.',
      '- effectiveTo: end date of rate/contract validity. Return in YYYY-MM-DD format, else null.',
      '- Keep freeText concise and practical for invoice creation.',
    ].join('\n');

    const outText = await this.callResponsesJson({ prompt, fileId });
    const match = (outText || '').match(/\{[\s\S]*\}/);
    if (!match) throw new Error('AI response did not contain JSON.');

    const parsed = JSON.parse(match[0]) as AIInvoiceContractFieldsResult;

    const normalizeString = (value: any): string | null => {
      if (value === null || value === undefined) return null;
      const str = String(value).trim();
      return str.length ? str : null;
    };

    const normalizeNumber = (value: any): number | null => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') return Number.isFinite(value) ? Number(value.toFixed(2)) : null;

      const raw = String(value).trim();
      const lower = raw.toLowerCase();
      const looksLikeDayRate =
        lower.includes('/day') ||
        lower.includes('per day') ||
        lower.includes('day rate') ||
        lower.includes('dagtarief') ||
        lower.includes('per dag') ||
        lower.includes('/dag') ||
        lower.includes('/jour') ||
        lower.includes('par jour') ||
        lower.includes('jourtarif');

      let cleaned = raw
        .replace(/\u00A0/g, ' ')
        .replace(/\s/g, '')
        .replace(/[^0-9,.-]/g, '');

      const hasComma = cleaned.includes(',');
      const hasDot = cleaned.includes('.');

      if (hasComma && hasDot) {
        const lastComma = cleaned.lastIndexOf(',');
        const lastDot = cleaned.lastIndexOf('.');
        if (lastComma > lastDot) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else {
          cleaned = cleaned.replace(/,/g, '');
        }
      } else if (hasComma && !hasDot) {
        const commaCount = (cleaned.match(/,/g) || []).length;
        if (commaCount > 1) {
          cleaned = cleaned.replace(/,/g, '');
        } else {
          const parts = cleaned.split(',');
          if (parts.length === 2 && parts[1].length === 3) {
            cleaned = parts[0] + parts[1];
          } else {
            cleaned = cleaned.replace(',', '.');
          }
        }
      } else if (hasDot && !hasComma) {
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (dotCount > 1) {
          cleaned = cleaned.replace(/\./g, '');
        } else {
          const parts = cleaned.split('.');
          if (parts.length === 2 && parts[1].length === 3) {
            cleaned = parts[0] + parts[1];
          }
        }
      }

      const num = Number(cleaned);
      if (!Number.isFinite(num)) return null;
      const hourly = looksLikeDayRate ? num / 8 : num;
      return Number(hourly.toFixed(2));
    };

    const normalizeDate = (value: any): string | null => {
      if (value === null || value === undefined) return null;
      const raw = String(value).trim();
      if (!raw) return null;

      const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoMatch) return raw;

      const dmyMatch = raw.match(/^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/);
      if (dmyMatch) {
        const day = Number(dmyMatch[1]);
        const month = Number(dmyMatch[2]);
        const year = Number(dmyMatch[3]);
        if (year >= 1900 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        }
      }

      const parsedDate = new Date(raw);
      if (!Number.isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().slice(0, 10);
      }
      return null;
    };

    const pickFirst = (...values: any[]): any => {
      for (const value of values) {
        if (value !== null && value !== undefined && String(value).trim() !== '') return value;
      }
      return null;
    };

    const extractedPrice = pickFirst(
      (parsed as any).pricePerHour,
      (parsed as any).hourlyRate,
      (parsed as any).ratePerHour,
      (parsed as any).hourRate,
      (parsed as any).uurprijs,
      (parsed as any).uurtarief,
      (parsed as any).tariefPerUur,
      (parsed as any).dayRate,
      (parsed as any).dagtarief,
    );

    const extractedBudget = pickFirst(
      (parsed as any).budget,
      (parsed as any).contractBudget,
      (parsed as any).contractValue,
      (parsed as any).totalContractValue,
      (parsed as any).maxBudget,
      (parsed as any).budgetPlafond,
      (parsed as any).notToExceed,
    );

    return {
      poNumber: normalizeString((parsed as any).poNumber),
      sesNumber: normalizeString((parsed as any).sesNumber),
      contractAgreementNumber: normalizeString((parsed as any).contractAgreementNumber),
      freeText: normalizeString((parsed as any).freeText),
      pricePerHour: normalizeNumber(extractedPrice),
      budget: normalizeNumber(extractedBudget),
      effectiveFrom: normalizeDate((parsed as any).effectiveFrom),
      effectiveTo: normalizeDate((parsed as any).effectiveTo),
      confidence: typeof (parsed as any).confidence === 'number' ? Number((parsed as any).confidence) : null,
      reason: normalizeString((parsed as any).reason),
    };
  }

  async suggestProjectInvoicingTimeFromPdfUrl(pdfUrlOrDataUrl: string): Promise<AIProjectInvoicingTimeResult> {
    await this.ensureOpenAIConfigured();
    const fileId = await this.uploadFileFromUrl(pdfUrlOrDataUrl);

    const prompt = [
      'You are an extraction assistant for project setup details from contracts/SOW/invoice instructions.',
      'Return ONLY compact valid JSON (no prose, no markdown).',
      'Extract values only when clearly stated; if unclear return null.',
      '',
      'Required JSON schema:',
      '{',
      '  "invoiceByDay": boolean|null,',
      '  "poRequired": boolean|null,',
      '  "paymentTermDays": number|null,',
      '  "timesheetRequired": boolean|null,',
      '  "timesheetType": string|null,',
      '  "hoursPerDay": number|null,',
      '  "confidence": number|null,',
      '  "reason": string|null',
      '}',
      '',
      'Extraction rules:',
      '- invoiceByDay: true only if invoicing is explicitly day-based (per day / man-day). false only if explicitly hourly. else null.',
      '- poRequired: true if PO reference/order number is mandatory for invoicing; false if explicitly not required; else null.',
      '- paymentTermDays: payment term in calendar days (integer).',
      '- timesheetRequired: true if timesheets/time registration are required.',
      '- timesheetType: concise type/system (e.g. "Tempo", "client portal", "internal").',
      '- hoursPerDay: explicit hours/day if mentioned (e.g. 8). If day-based invoicing is stated but hours/day absent, return 8.',
    ].join('\n');

    const outText = await this.callResponsesJson({ prompt, fileId });
    const match = (outText || '').match(/\{[\s\S]*\}/);
    if (!match) throw new Error('AI response did not contain JSON.');

    const parsed = JSON.parse(match[0]) as AIProjectInvoicingTimeResult;

    const normalizeString = (value: any): string | null => {
      if (value === null || value === undefined) return null;
      const str = String(value).trim();
      return str.length ? str : null;
    };

    const normalizeBoolean = (value: any): boolean | null => {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'boolean') return value;
      const lower = String(value).trim().toLowerCase();
      if (['true', 'yes', 'y', '1', 'required', 'mandatory'].includes(lower)) return true;
      if (['false', 'no', 'n', '0', 'optional', 'not required'].includes(lower)) return false;
      return null;
    };

    const normalizeInteger = (value: any, min?: number, max?: number): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const num = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.').replace(/[^0-9.-]/g, ''));
      if (!Number.isFinite(num)) return null;
      const intVal = Math.trunc(num);
      if (typeof min === 'number' && intVal < min) return null;
      if (typeof max === 'number' && intVal > max) return null;
      return intVal;
    };

    const invoiceByDay = normalizeBoolean((parsed as any).invoiceByDay);
    const normalizedHours = normalizeInteger((parsed as any).hoursPerDay, 1, 12);

    return {
      invoiceByDay,
      poRequired: normalizeBoolean((parsed as any).poRequired),
      paymentTermDays: normalizeInteger((parsed as any).paymentTermDays, 1, 180),
      timesheetRequired: normalizeBoolean((parsed as any).timesheetRequired),
      timesheetType: normalizeString((parsed as any).timesheetType),
      hoursPerDay: normalizedHours ?? (invoiceByDay === true ? 8 : null),
      confidence: typeof (parsed as any).confidence === 'number' ? Number((parsed as any).confidence) : null,
      reason: normalizeString((parsed as any).reason),
    };
  }

  // ====================== LOGBOOK (company.uuid) ======================
  /**
   * Haalt de eerste logregel op met category 'AI-prompt-for-invoices' voor de gegeven companyUuid.
   * Retourneert de description als string of null.
   */
  async fetchCompanyLogbookPrompt(companyUuid: string | undefined | null): Promise<string | null> {
    if (!companyUuid) return null;

    try {
      await firstValueFrom(this.store.dispatch(new GetLogItemsByObjectId(companyUuid)));
      const all: LogItemsForObject[] = this.store.selectSnapshot(LogbookState.LogItems);
      const forCompany = all?.find(x => x.objectId === companyUuid);
      const match: LogItem | undefined = forCompany?.logItems?.find(
        (li) => (li?.category || '').toLowerCase() === 'ai-classifier'
      );
      return match?.description?.trim() || null;
    } catch {
      return null;
    }
  }

  // (obsolete helpers removed — using uploadFileFromUrl() and validateTimesheetPdfWithResponses())

  // =====================================================================
  // ======================   CLASSIFICATIE (allocaties)   ================
  // =====================================================================

  /**
   * AI-allocaties o.b.v. context (overheads & actieve budgets), beschrijving en logbook.
   * targetId MOET altijd overeenkomen met bestaande ids.
   * Volgorde: company-tags/logbook → overhead → actieve budgets.
   */
  async classifyAllocations(args: {
    invoiceAmountExcl: number;
    budgets: Array<{ id: string; displayName: string; archived?: boolean; startDate?: string | null; endDate?: string | null }>;
    overheadCategories: Array<{ id: string | number; displayName: string }>;
    // extra context (niet rechtstreeks gelinkt in deze modal, maar nuttig als hint)
    projects?: Array<{ id: string; displayName: string; archived?: boolean }>;
    employees?: Array<{ id: string; displayName: string; archived?: boolean }>;

    description?: string | null;
    contactName?: string | null;
    aiClassifierLogbook?: string | null; // optioneel vooraf meegegeven
    company?: { id?: string; name?: string } | null;
    companyContactTags?: unknown;

    file?: { kind: 'pdf' | 'image'; dataUrl: string } | null;
  }): Promise<AIAllocationsResult> {
    await this.ensureOpenAIConfigured();

    // Active budgets filteren
    const today = new Date();
    const isActiveBudget = (b: { archived?: boolean; startDate?: string | null; endDate?: string | null }): boolean => {
      if (b.archived) return false;
      const startOk = !b.startDate || new Date(b.startDate) <= today;
      const endOk = !b.endDate || new Date(b.endDate) >= today;
      return startOk && endOk;
    };
    const activeBudgets = (args.budgets || []).filter(isActiveBudget);

    // Allowed ids
    const budgetIdSet = new Set<string>(activeBudgets.map(b => String(b.id)));
    const overheadIdSet = new Set<string>((args.overheadCategories || []).map(o => String(o.id)));
  const projectIdSet = new Set<string>((args.projects || []).map(p => String(p.id)));
  const employeeIdSet = new Set<string>((args.employees || []).map(e => String(e.id)));

    // Company logbook ophalen indien niet meegegeven
    const logbookHint =
      args.aiClassifierLogbook ??
      (await this.fetchCompanyLogbookPrompt(args.company?.id ?? null));

    // Prompt override
    const custom = this.getCustomPrompt();

    // Prompt opbouwen
    const blocks: any[] = [];

    if (custom) {
      blocks.push({ type: 'text', text: `# CUSTOM PROMPT (OVERRIDE)\n${custom}\n\n# --- END CUSTOM PROMPT ---\n` });
    }

    if(!custom){

    const logbookHintText = logbookHint ? JSON.stringify(logbookHint) : 'null';

    blocks.push({
      type: 'text',
      text: [
        'ONLY if you do not have a CUSTOM prompt, use the following (except for the IDs below.):',
  'ROLE: You are an accounting allocation assistant for Alpine Digital.',
        'CONSTRAINTS:',
        '- If logbookHint clearly indicate a classification, use that FIRST.',
        `logbookHint: ${logbookHintText}`,
        '- NEXT: try to match **overhead categories**.',
        '- NEXT: try **(ACTIVE) personal budgets**.',
        '- NEXT: for sale invoices only, try to match **PROJECTS** (only match 1 project).',
        '- Use ONLY existing items from the provided lists — NEVER invent IDs.',
        '- Sum of allocation amounts must be = invoiceAmountExcl (rounded to 2 decimals).',
        '- Do NOT guess: omit uncertain lines.',
        '',
        `invoiceAmountExcl: ${args.invoiceAmountExcl}`,
        '',
        `ALWAYS Use the following information to map into (budgets, overhead, projects, employees), even if you have a custom prompt:`,

        'ACTIVE_BUDGETS:',
        JSON.stringify(activeBudgets.map(e => e.id + ":" + e.displayName + " -> from " + e.startDate+ " untill " + e.endDate) ?? []),
        '',
        'OVERHEAD_CATEGORIES (id, displayName):',
        JSON.stringify(args.overheadCategories ?? []),
        '',
        'EXTRA CONTEXT — PROJECTS (id, displayName, archived?):',
        JSON.stringify(args.projects ?? []),
        '',
        'EXTRA CONTEXT — EMPLOYEES (id, displayName, archived?):',
        JSON.stringify(args.employees ?? []),
        '',
        'ALWAYS, WITHOUT EXCEPTIONS: OUTPUT STRICT JSON ONLY.(no prose), make SURE to insert the correct amount, type, existing target and note. DOUBLECHECK your information. The JSON-schema:',
        JSON.stringify({
          allocations: [
            { amount: 1.0, type: 'budget|overheadCategory|project|employee', targetId: '', note: '' },
            { amount: 1.0, type: '', targetId: '', note: '' }
          ],
          confidence: 1,
          reason: 'short rationale'
        })
      ].join('\n')
    });
  }

    // Factuur toevoegen (optioneel)
    if (args.file?.kind === 'image') {
      blocks.push({ type: 'image_url', image_url: { url: args.file.dataUrl } });
    }

    // Prompt logging
    console.groupCollapsed('%c[AI CLASSIFIER PROMPT]', 'color:#00bfa5;font-weight:bold;');
    console.log('Model:', '(server-configured)');
    console.log('Endpoint:', `${environment.baseUrl}/openai/chat/allocations`);
    console.log('---- FULL PROMPT ----');
    console.log(JSON.stringify(blocks, null, 2));
    console.groupEnd();


    // EXTRA DEBUG LOG — duidelijk zien welke logbook data meegaat
    if (args.aiClassifierLogbook) {
      console.groupCollapsed('%c[AI CLASSIFIER LOGBOOK INCLUDED]', 'color:#4caf50;font-weight:bold;');
      console.log(args.aiClassifierLogbook);
      console.groupEnd();
    } else {
      console.info('%c[AI CLASSIFIER LOGBOOK]', 'color:#ff9800;', 'Geen logbook data gevonden.');
    }

    // Call
    const data = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/chat/allocations`, { blocks }, this.jsonOptions()));

    // Response logging
    console.groupCollapsed('%c[AI RAW RESPONSE]', 'color:#ff9800;font-weight:bold;');
    console.log(JSON.stringify(data, null, 2));
    console.groupEnd();

    const json = this.safeParseAllocations(data?.choices?.[0]?.message?.content);

    // Post-validatie/normalisatie
    const max = Math.max(0, Number(args.invoiceAmountExcl || 0));
    let total = 0;

    const cleaned = (json.allocations || [])
      .map((l: any) => {
        const amount = Math.max(0, Number(l?.amount || 0));
        const rawType = (l?.type || '').toString();
        const allowedTypes = ['budget', 'overheadCategory', 'project', 'employee'];
        const type = allowedTypes.includes(rawType) ? (rawType as any) : 'overheadCategory';
        const targetId = String(l?.targetId || '').trim();

          const valid =
            (type === 'budget' && budgetIdSet.has(targetId)) ||
            (type === 'overheadCategory' && overheadIdSet.has(targetId)) ||
            (type === 'project' && projectIdSet.has(targetId)) ||
            (type === 'employee' && employeeIdSet.has(targetId));
          if (!valid) return null;

      const remaining = +(max - total).toFixed(2);
      if (remaining <= 0) return null;

      const useAmount = amount > remaining ? remaining : amount;
      total = +(total + useAmount).toFixed(2);

      return {
        amount: useAmount,
        type,
        targetId,
        note: (l?.note ?? null) ? String(l.note) : null,
      } as AllocationLine;
    })
    .filter((l) => !!l && l.amount > 0);


    return {
      allocations: cleaned,
      confidence: typeof json.confidence === 'number' ? Math.max(0, Math.min(1, json.confidence)) : null,
      reason: typeof json.reason === 'string' ? json.reason : null
    };
  }

  private safeParseAllocations(content?: string): AIAllocationsResult {
    const text = content || '';
    const match = text.match(/\{[\s\S]*\}/);
    if (!match) return { allocations: [] };
    try {
      const parsed = JSON.parse(match[0]);
      if (!Array.isArray(parsed.allocations)) parsed.allocations = [];
      return parsed;
    } catch {
      return { allocations: [] };
    }
  }

  async suggestMarAccountLinks(args: {
    overheadCategories: Array<{ id: string; displayName: string }>;
    marAccounts: Array<{ accountNumber: string; accountName: string }>;
  }): Promise<AIMarAccountLinkSuggestion[]> {
    await this.ensureOpenAIConfigured();

    const categories = (args.overheadCategories || [])
      .map((c) => ({ id: String(c?.id || '').trim(), displayName: String(c?.displayName || '').trim() }))
      .filter((c) => c.id.length > 0 && c.displayName.length > 0);

    const accounts = (args.marAccounts || [])
      .map((a) => ({ accountNumber: String(a?.accountNumber || '').trim(), accountName: String(a?.accountName || '').trim() }))
      .filter((a) => a.accountNumber.length > 0);

    if (categories.length === 0 || accounts.length === 0) {
      return [];
    }

    const blocks: any[] = [
      {
        type: 'text',
        text: [
          'ROLE: Je koppelt overheadkosten aan MAR rekeningen.',
          'Doel: Maak zo goed mogelijke 1-op-1 voorstellen op basis van naam/semantiek.',
          'REGELS:',
          '- Gebruik ALLEEN ids/accountnummers uit de gegeven lijsten.',
          '- Gebruik elke accountNumber maximaal één keer.',
          '- Als je geen redelijke match ziet: laat die overheadCategorie weg.',
          '- Geen uitleg buiten JSON.',
          '',
          'OVERHEAD_CATEGORIES (id, displayName):',
          JSON.stringify(categories),
          '',
          'MAR_ACCOUNTS (accountNumber, accountName):',
          JSON.stringify(accounts),
          '',
          'Output STRICT JSON:',
          JSON.stringify({
            links: [
              {
                overheadCategoryId: 'id-from-overhead-categories',
                accountNumber: 'accountNumber-from-mar-accounts',
                reason: 'short',
                confidence: 0.8,
              },
            ],
          }),
        ].join('\n'),
      },
    ];

    const data = await firstValueFrom(this.http.post<any>(`${environment.baseUrl}/openai/chat/allocations`, { blocks }, this.jsonOptions()));
    const content = data?.choices?.[0]?.message?.content || '';
    const match = String(content).match(/\{[\s\S]*\}/);
    if (!match) {
      return [];
    }

    try {
      const parsed = JSON.parse(match[0]);
      const links = Array.isArray(parsed?.links) ? parsed.links : [];

      const validCategoryIds = new Set(categories.map((c) => c.id));
      const validAccountNumbers = new Set(accounts.map((a) => a.accountNumber));
      const usedAccountNumbers = new Set<string>();

      return links
        .map((link: any) => ({
          overheadCategoryId: String(link?.overheadCategoryId || '').trim(),
          accountNumber: String(link?.accountNumber || '').trim(),
          reason: link?.reason ? String(link.reason) : null,
          confidence: typeof link?.confidence === 'number' ? Math.max(0, Math.min(1, Number(link.confidence))) : null,
        } as AIMarAccountLinkSuggestion))
        .filter((link) => {
          if (!validCategoryIds.has(link.overheadCategoryId)) {
            return false;
          }
          if (!validAccountNumbers.has(link.accountNumber)) {
            return false;
          }
          if (usedAccountNumbers.has(link.accountNumber)) {
            return false;
          }
          usedAccountNumbers.add(link.accountNumber);
          return true;
        });
    } catch {
      return [];
    }
  }
}
